import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { sectionRenderer } from '@theme/section-renderer';
import { getViewParameterValue, yieldToMainThread } from '@theme/utilities';
// Colour sale dots that follow the selected size (Teamwork 41853768).
import { keepRating, readRatingHtml, sameSizeColourUrl } from '@theme/colour-sale-dots';

/** @type {boolean} */
let hasPopstateListener = false;

/**
 * Which product detail accordions (Highlights, Description...) are open, by their summary text -
 * the same for every colour. The colour swap replaces the whole section, which put them back to
 * their defaults: an open Description closed again and the page jumped (Teamwork 41853774).
 * @param {Element} root
 * @returns {Map<string, boolean>}
 */
/**
 * Keeps the size picker at least `height` tall for the rest of the page view.
 * @param {ParentNode} root
 * @param {number} height
 */
function holdSizePickerHeight(root, height) {
  const picker = root.querySelector('variant-picker');
  if (picker instanceof HTMLElement && height > 0) picker.style.minHeight = `${height}px`;
}

function readAccordionState(root) {
  const state = new Map();
  for (const details of root.querySelectorAll('accordion-custom details')) {
    const key = details.querySelector('summary')?.textContent?.trim();
    if (key) state.set(key, /** @type {HTMLDetailsElement} */ (details).open);
  }
  return state;
}

/**
 * Re-applies {@link readAccordionState} to the new section - now, after the accordions'
 * connectedCallback has set their default state, and once more after the timer in which
 * assets/accordion-custom.js opens the first accordion when none is open.
 * @param {Element} root
 * @param {Map<string, boolean>} state
 */
function restoreAccordionState(root, state) {
  if (state.size === 0) return;
  const apply = () => {
    for (const details of root.querySelectorAll('accordion-custom details')) {
      const key = details.querySelector('summary')?.textContent?.trim();
      if (key && state.has(key)) /** @type {HTMLDetailsElement} */ (details).open = Boolean(state.get(key));
    }
  };
  apply();
  setTimeout(apply, 0);
}

/** The pathname of the product (colour) on screen - updated when a colour change replaces the URL. */
let shownProductPath = '';

/**
 * Keeps warmed image objects alive until they finish loading - a plain
 * local `new Image()` with no other reference can be garbage-collected
 * before its request completes, silently dropping the warm-up.
 * @type {Set<HTMLImageElement>}
 */
const pendingImagePreloads = new Set();

/**
 * Root cause of the 3-3.5s swatch-to-image delay (confirmed via Network
 * panel + performance timing): a colour swatch here is a different
 * PRODUCT, not a variant, so #loadProduct below fetches that product's
 * full page HTML (~1.8MB / 7000+ DOM nodes on this store, confirmed
 * live) and only once that resolves, gets parsed, and morph() finishes
 * diffing it in does the browser even discover the new main image - by
 * itself measured at 1.5-2.5s, before the image's own network time. The
 * sibling's own main image URL/srcset is already known at render time
 * (see snippets/product-stitching-swatch.liquid's data-image-src /
 * data-image-srcset - same pattern assets/product-card-stitching.js
 * already uses for PLP card swatches), so this swaps the PDP's own
 * currently-visible main image directly from that data immediately on
 * click, without waiting on the page fetch/morph at all. The slower
 * fetch+morph flow is left completely intact below to correctly update
 * everything else (price, title, variant picker, full gallery, SEO
 * tags) - only the one visible image is fast-pathed.
 * @returns {HTMLImageElement | null}
 */
function getVisibleMainImage() {
  // The stitching block can be its own section, separate from the main
  // product-information section that actually holds the gallery (see
  // #loadProduct's own productInfoSection/section split below) - so
  // this looks page-wide, same as #loadProduct does for that section.

  // When the gallery is in grid mode (data-presentation="grid") the
  // slideshow component is hidden on desktop; the actually-visible first
  // image is the first <li> in .media-gallery__grid. Swapping the hidden
  // slideshow's image (the old code path) left the grid untouched and
  // made it appear as though the product hadn't changed until the full
  // morph landed. Check grid mode first, fall back to slideshow.
  const gallery = document.querySelector('media-gallery');
  if (gallery?.dataset.presentation === 'grid') {
    const gridFirstImg = gallery.querySelector(
      '.media-gallery__grid li:first-child img.product-media__image'
    );
    if (gridFirstImg) return gridFirstImg;
  }

  const slideshow = document.querySelector('.product-information slideshow-component, slideshow-component');

  // The new product's own first/cover image is what we have data for -
  // reset to slide 0 first (mirroring product-card-stitching.js's own
  // `slideshow.select(0, ...)` before its equivalent swap) so we don't
  // overwrite whatever OTHER slide the shopper had scrolled to.
  if (slideshow && 'select' in slideshow && typeof slideshow.select === 'function') {
    slideshow.select(0, undefined, { animate: false });
  }

  const activeSlide = slideshow?.querySelector('slideshow-slide:not([hidden])') || slideshow;
  return activeSlide?.querySelector('img.product-media__image') ?? null;
}

/**
 * @param {HTMLImageElement} img
 * @param {string} src
 * @param {string} [srcset]
 * @param {string} [alt]
 */
function applyImageSource(img, src, srcset, alt) {
  if (srcset) {
    img.srcset = srcset;
  } else {
    img.removeAttribute('srcset');
  }
  img.src = src;
  if (alt) img.alt = alt;
}

/**
 * Warms the swatch's target image on hover too - most clicks are
 * preceded by a hover, so this often finishes the image download before
 * the click even happens. Setting `srcset`/`sizes` on a detached Image
 * (not just `src`) makes the browser run the same responsive-selection
 * it would for the real <img>, so this warms the exact variant that
 * will actually be used. Duplicate/no-op if that URL is already
 * loading or cached.
 * @param {HTMLAnchorElement} link
 */
function preloadStitchingImage(link) {
  const srcset = link.dataset.imageSrcset;
  const src = link.dataset.imageSrc;
  if (!src && !srcset) return;

  const image = new Image();
  pendingImagePreloads.add(image);
  const cleanup = () => pendingImagePreloads.delete(image);
  image.addEventListener('load', cleanup, { once: true });
  image.addEventListener('error', cleanup, { once: true });
  if (srcset) {
    image.sizes = '(min-width: 750px) 50vw, 100vw';
    image.srcset = srcset;
  }
  if (src) image.src = src;
}

/**
 * @returns {Element | null | undefined}
 */
function getProductInfoSection() {
  return (
    document.querySelector('.product-information, [data-testid="product-information"]')?.closest('.shopify-section') ||
    document.querySelector('variant-picker')?.closest('.shopify-section')
  );
}

/**
 * Starts fetching the target product's main section on hover, using the
 * same sectionRenderer call #loadProduct makes on click. sectionRenderer
 * dedupes concurrent calls for the same URL internally (its own
 * #pendingPromises map), so if the fetch is still in flight when the
 * click actually happens, #loadProduct's own call below reuses this
 * exact same in-flight request instead of starting a second one - on a
 * normal hover-then-click, the ~1.1s section round trip (confirmed live
 * - the dominant remaining cost now that the payload itself is just one
 * section instead of the full page) often finishes before the click even
 * happens.
 * @param {HTMLAnchorElement} link
 */
function preloadStitchingSection(link) {
  const productInfoSection = getProductInfoSection();
  if (!productInfoSection?.id) return;

  const url = new URL(colourTargetUrl(link), window.location.origin);
  const viewParam = getViewParameterValue();
  if (viewParam) url.searchParams.set('view', viewParam);

  sectionRenderer.getSectionHTML(productInfoSection.id, false, url).catch(() => {
    // Swallowed - #loadProduct's own fetch on click will retry/report.
  });
}

/**
 * The picked colour's URL in the currently selected size, when that colour has it in stock
 * (assets/colour-sale-dots.js) - so the size doesn't jump back to the first one on a colour change.
 * @param {HTMLAnchorElement} link - A colour swatch link.
 * @returns {string}
 */
function colourTargetUrl(link) {
  const container = link.closest('[data-colour-sale-dots]');
  const item = link.closest('.pdp-colour-swatch__color-item');
  const variantInput = getProductInfoSection()?.querySelector('form[data-type="add-to-cart-form"] input[name="id"], input[name="id"]');
  const variantId = variantInput instanceof HTMLInputElement ? variantInput.value : null;
  return sameSizeColourUrl(container, item, link.href, variantId);
}

/**
 * PDP colour stitching — swaps the product-information section via the
 * Section Rendering API when a stitched colour swatch is selected.
 */
class ProductPdpStitching extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('pointerenter', this.#handlePointerEnter, { capture: true });
    this.#ensurePopstateListener();
    this.#ensureActiveSwatch();

    // See ThemeEvents.pdpSectionFetchStart's own doc comment: this colour
    // swap and variant-picker.js's own variant fetch both replace the same
    // product-information section from independent, uncoordinated fetches.
    // If a normal variant change (e.g. size) starts while our own colour
    // swap is still in flight, abort it so its now-stale response (captured
    // before that variant change) never lands and overwrites what the
    // variant change is about to render.
    document.addEventListener(ThemeEvents.pdpSectionFetchStart, this.#onOtherSectionFetchStart);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('pointerenter', this.#handlePointerEnter, { capture: true });
    this.#abortController?.abort();
    this.#abortController = null;
    document.removeEventListener(ThemeEvents.pdpSectionFetchStart, this.#onOtherSectionFetchStart);
  }

  /**
   * @param {CustomEvent<{source: string}>} event
   */
  #onOtherSectionFetchStart = (event) => {
    if (event.detail?.source === 'product-pdp-stitching') return;
    this.#abortController?.abort();
  };

  /**
   * Warms the swatch's target image on hover too - most clicks are
   * preceded by a hover, so this often finishes the image download
   * before the click even happens. Duplicate/no-op if the same URL is
   * already loading or cached (the browser dedupes identical requests).
   * @param {PointerEvent} event
   */
  #handlePointerEnter = (event) => {
    const link = event.target instanceof Element ? event.target.closest('.pdp-colour-swatch__color-item a') : null;
    if (link instanceof HTMLAnchorElement && this.contains(link)) {
      preloadStitchingImage(link);
      preloadStitchingSection(link);
    }
  };

  #ensureActiveSwatch() {
    const hasActive = this.querySelector('.pdp-colour-swatch__color-item--active');
    if (hasActive) return;

    const currentPath = window.location.pathname;
    const links = Array.from(this.querySelectorAll('.pdp-colour-swatch__color-item a'));

    let activeLink = links.find((a) => {
      const href = a.getAttribute('href');
      if (!href) return false;
      try {
        return new URL(href, window.location.origin).pathname === currentPath;
      } catch (e) {
        return false;
      }
    });

    if (activeLink) {
      const item = activeLink.closest('.pdp-colour-swatch__color-item');
      if (item) {
        item.classList.add('pdp-colour-swatch__color-item--active');
        activeLink.setAttribute('aria-current', 'true');
      }
    }
  }

  #ensurePopstateListener() {
    if (hasPopstateListener) return;
    hasPopstateListener = true;
    shownProductPath = window.location.pathname;

    // Colour changes replace the history entry (see #syncDocument), so a popstate here is only an
    // entry another script or an in-page #anchor added. Reload just when it leads to a different
    // product URL than the one on screen - not for a #hash entry on the same product.
    window.addEventListener('popstate', () => {
      if (!document.querySelector('product-pdp-stitching')) return;
      if (window.location.pathname === shownProductPath) return;
      window.location.reload();
    });
  }

  /**
   * @param {Event} event
   */
  #handleClick = (event) => {
    const link =
      event.target instanceof Element ? event.target.closest('.pdp-colour-swatch__color-item a') : null;

    if (!(link instanceof HTMLAnchorElement) || !this.contains(link)) return;

    const item = link.closest('.pdp-colour-swatch__color-item');
    if (item?.classList.contains('pdp-colour-swatch__color-item--active')) return;

    event.preventDefault();

    // Optimistic UI update: instantly highlight active border on clicked swatch for ALL blocks
    const clickedHref = link.getAttribute('href');
    const colorName =
      link.dataset.colorName ||
      item?.querySelector('.visually-hidden')?.textContent?.trim() ||
      link.getAttribute('title');

    document.querySelectorAll('product-pdp-stitching').forEach((block) => {
      block.querySelectorAll('.pdp-colour-swatch__color-item').forEach((el) => {
        el.classList.remove('pdp-colour-swatch__color-item--active');
        const a = el.querySelector('a');
        if (a) a.removeAttribute('aria-current');
      });

      if (clickedHref) {
        const matchingLink = block.querySelector(`a[href="${clickedHref}"]`);
        if (matchingLink) {
          const matchingItem = matchingLink.closest('.pdp-colour-swatch__color-item');
          if (matchingItem) {
            matchingItem.classList.add('pdp-colour-swatch__color-item--active');
            matchingLink.setAttribute('aria-current', 'true');
          }
        }
      }
    });

    if (colorName) {
      document.querySelectorAll('.product-stitching__colour-value').forEach((el) => {
        el.textContent = colorName;
      });
    }

    // Swap the visible main image immediately from the swatch's own
    // data, instead of waiting for the (confirmed multi-second) page
    // fetch + morph below to discover and load it. Falls through to the
    // preload warm-up when this product's block has no gallery image
    // element to swap directly (e.g. no media at all).
    const mainImage = getVisibleMainImage();
    if (mainImage && (link.dataset.imageSrc || link.dataset.imageSrcset)) {
      applyImageSource(mainImage, link.dataset.imageSrc, link.dataset.imageSrcset, colorName);
    } else {
      preloadStitchingImage(link);
    }

    this.#loadProduct(colourTargetUrl(link));
  };

  /**
   * @param {string} productUrl
   */
  async #loadProduct(productUrl) {
    const sectionId = this.dataset.sectionId;
    const section = this.closest('.shopify-section');

    if (!sectionId || !section) return;

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    // Tell variant-picker.js (if a variant fetch is in flight) to abort
    // itself - see ThemeEvents.pdpSectionFetchStart's doc comment.
    document.dispatchEvent(
      new CustomEvent(ThemeEvents.pdpSectionFetchStart, { detail: { source: 'product-pdp-stitching' } })
    );

    this.setAttribute('aria-busy', 'true');
    section.setAttribute('aria-busy', 'true');

    try {
      // 1. Locate current main product information section (and the other
      // optional sections below), same as before.
      const productInfoSection = getProductInfoSection();
      const b2bSection = document.querySelector('.b2b-variants-list-wrapper')?.closest('.shopify-section');
      const cartonSection = document.querySelector('.product-carton-quantities')?.closest('.shopify-section');

      const needsStitchingSection = Boolean(section && (!productInfoSection || !productInfoSection.contains(section)));
      const needsB2bSection = Boolean(
        b2bSection && (!productInfoSection || !productInfoSection.contains(b2bSection)) && (!section || !section.contains(b2bSection))
      );
      const needsCartonSection = Boolean(
        cartonSection &&
          (!productInfoSection || !productInfoSection.contains(cartonSection)) &&
          (!section || !section.contains(cartonSection))
      );

      // Root cause of the multi-second delay (confirmed via Network panel
      // and performance timing): this used to fetch() the plain product
      // URL, which returns the ENTIRE page - header, footer, every app
      // embed, every unrelated section (~1.8MB / 7000+ DOM nodes on this
      // store, confirmed live) - then parsed and diffed all of it just to
      // reach the one product-information section that actually needed
      // updating; that parse+diff alone measured at 1.5-2.5s, on top of
      // the page's own network time. Fetches only the section(s) actually
      // needed via Shopify's own per-section Section Rendering API
      // (`?section_id=<id>`), the same mechanism assets/section-renderer.js
      // already provides and other theme scripts (facets.js,
      // predictive-search.js, cart-discount.js) already use elsewhere -
      // each response is just that one section's markup, so both the
      // network payload and the DOM parse/diff work shrink by roughly two
      // orders of magnitude. Fetched in parallel since there can be up to
      // four independent sections to update.
      const targetUrl = new URL(productUrl, window.location.origin);
      const viewParam = getViewParameterValue();
      if (viewParam) targetUrl.searchParams.set('view', viewParam);

      /** @type {Map<string, Promise<string>>} */
      const htmlPromiseBySectionId = new Map();
      const queueSection = (el) => {
        if (!el?.id || htmlPromiseBySectionId.has(el.id)) return;
        htmlPromiseBySectionId.set(el.id, sectionRenderer.getSectionHTML(el.id, false, new URL(targetUrl)));
      };

      queueSection(productInfoSection);
      if (needsStitchingSection) queueSection(section);
      if (needsB2bSection) queueSection(b2bSection);
      if (needsCartonSection) queueSection(cartonSection);

      if (htmlPromiseBySectionId.size === 0) return;

      const ids = [...htmlPromiseBySectionId.keys()];
      const htmlResults = await Promise.all(htmlPromiseBySectionId.values());
      if (signal.aborted) return;

      /** @type {Map<string, Document>} */
      const docBySectionId = new Map();
      ids.forEach((id, i) => {
        const doc = new DOMParser().parseFromString(htmlResults[i], 'text/html');
        doc.querySelector('overflow-list[defer]')?.removeAttribute('defer');
        docBySectionId.set(id, doc);
      });

      const getDoc = (el) => (el?.id ? docBySectionId.get(el.id) : undefined);

      // 2. Locate corresponding new main product section in its own doc
      const productInfoDoc = getDoc(productInfoSection);
      let newProductInfoSection =
        productInfoSection?.id && productInfoDoc ? productInfoDoc.getElementById(productInfoSection.id) : null;
      if (!newProductInfoSection && productInfoDoc) {
        newProductInfoSection =
          productInfoDoc.querySelector('.product-information, [data-testid="product-information"]')?.closest('.shopify-section') ||
          productInfoDoc.querySelector('variant-picker')?.closest('.shopify-section') ||
          productInfoDoc.querySelector('.shopify-section') ||
          productInfoDoc.body.firstElementChild;
      }

      // 3. Full DOM replacement of main product section to cleanly reset all custom elements
      if (productInfoSection && newProductInfoSection) {
        const accordionState = readAccordionState(productInfoSection);
        const sizePickerHeight = productInfoSection.querySelector('variant-picker')?.getBoundingClientRect().height ?? 0;
        const ratingHtml = readRatingHtml(productInfoSection);
        productInfoSection.innerHTML = newProductInfoSection.innerHTML;
        for (const attr of Array.from(newProductInfoSection.attributes)) {
          productInfoSection.setAttribute(attr.name, attr.value);
        }
        restoreAccordionState(productInfoSection, accordionState);
        // Before the next paint: the title keeps the longest colour title's height
        // (snippets/stitching-title-reserve.liquid) and the size picker doesn't get shorter, so a colour
        // with fewer sizes or a shorter title doesn't move what's below (Teamwork 41853774).
        window.tssReservePdpTitle?.();
        holdSizePickerHeight(productInfoSection, sizePickerHeight);
        keepRating(productInfoSection, ratingHtml);

        // Deduplicate b2b actions block if multiple exist
        const b2bActionsList = productInfoSection.querySelectorAll('.b2b-details-actions');
        if (b2bActionsList.length > 1) {
          for (let i = 1; i < b2bActionsList.length; i++) {
            const wrapper = b2bActionsList[i].closest('[id^="shopify-block-"]') || b2bActionsList[i];
            wrapper.remove();
          }
        }
      }

      // 4. Handle stitching section if it's separate from productInfoSection
      if (needsStitchingSection) {
        const stitchingDoc = getDoc(section);
        let newStitchingSection = section.id && stitchingDoc ? stitchingDoc.getElementById(section.id) : null;
        if (!newStitchingSection && stitchingDoc) {
          newStitchingSection =
            stitchingDoc.querySelector('product-pdp-stitching')?.closest('.shopify-section') ||
            stitchingDoc.querySelector('.shopify-section') ||
            stitchingDoc.body.firstElementChild;
        }

        if (newStitchingSection) {
          section.innerHTML = newStitchingSection.innerHTML;
          for (const attr of Array.from(newStitchingSection.attributes)) {
            section.setAttribute(attr.name, attr.value);
          }
        }
      }

      // 5. Update B2B variants list section if present
      if (needsB2bSection) {
        const b2bDoc = getDoc(b2bSection);
        let newB2bSection = b2bSection.id && b2bDoc ? b2bDoc.getElementById(b2bSection.id) : null;
        if (!newB2bSection && b2bDoc) {
          newB2bSection = b2bDoc.querySelector('.b2b-variants-list-wrapper')?.closest('.shopify-section');
        }

        if (newB2bSection) {
          const b2bBody = b2bSection.querySelector('.b2b-variants-list__body');
          const newB2bBody = newB2bSection.querySelector('.b2b-variants-list__body');

          if (b2bBody && newB2bBody) {
            b2bBody.innerHTML = newB2bBody.innerHTML;
          } else {
            const b2bWrapper = b2bSection.querySelector('.b2b-variants-list-wrapper');
            const newB2bWrapper = newB2bSection.querySelector('.b2b-variants-list-wrapper');
            if (b2bWrapper && newB2bWrapper) {
              b2bWrapper.innerHTML = newB2bWrapper.innerHTML;
            } else {
              b2bSection.innerHTML = newB2bSection.innerHTML;
            }
          }
        }
      }

      // Always re-initialise the B2B qty inputs after a colour-swatch navigation,
      // regardless of whether the B2B section was swapped above. Using
      // requestAnimationFrame ensures the browser has committed the new DOM nodes
      // before the listeners are re-attached.
      // We call initB2BVariantsList directly (when available) AND dispatch the
      // custom event so both code-paths are covered.
      requestAnimationFrame(() => {
        if (typeof window.initB2BVariantsList === 'function') {
          window.initB2BVariantsList();
        }
        document.dispatchEvent(new CustomEvent('b2b:updated'));
      });

      // 6. Update Carton Quantities block if present
      if (needsCartonSection) {
        const cartonDoc = getDoc(cartonSection);
        let newCartonSection = cartonSection.id && cartonDoc ? cartonDoc.getElementById(cartonSection.id) : null;
        if (!newCartonSection && cartonDoc) {
          newCartonSection = cartonDoc.querySelector('.product-carton-quantities')?.closest('.shopify-section');
        }

        if (newCartonSection) {
          const cartonWrappers = cartonSection.querySelectorAll('.product-carton-quantities');
          const newCartonWrappers = newCartonSection.querySelectorAll('.product-carton-quantities');
          cartonWrappers.forEach((wrapper, index) => {
            if (newCartonWrappers[index]) {
              wrapper.replaceWith(newCartonWrappers[index]);
            }
          });
        }
      }

      const activeMainSection = productInfoSection || section;
      const activeDoc = getDoc(activeMainSection) || productInfoDoc;

      // When the stitching section is separate, pass its doc as extraDoc so
      // that #dispatchVariantUpdate can build a merged html document covering
      // both sections. This lets product-price / product-availability blocks
      // placed inside the stitching section find their updated markup even
      // though the primary event html comes from productInfoSection.
      const stitchingExtraDoc = needsStitchingSection ? getDoc(section) : undefined;

      if (activeDoc && activeMainSection) {
        this.#syncDocument(productUrl, activeMainSection, activeDoc);
        this.#dispatchVariantUpdate(activeMainSection, activeDoc, stitchingExtraDoc);
      }

    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error('PDP colour stitching failed:', error);
      // replace, not assign: like a successful colour change (see #syncDocument), the fallback full
      // load must not add a history entry either, so Back still returns to the previous page.
      window.location.replace(productUrl);
    } finally {
      section.removeAttribute('aria-busy');
      section.querySelector('product-pdp-stitching')?.removeAttribute('aria-busy');
    }
  }

  /**
   * @param {string} productUrl
   * @param {Element} section
   * @param {Document} doc
   */
  #syncDocument(productUrl, section, doc) {
    const heading = section.querySelector('.product-details h1, .product-details [role="heading"]');
    const titleText = heading?.textContent?.trim();

    if (titleText) {
      const shopSuffix = document.title.includes('–')
        ? document.title.slice(document.title.indexOf('–'))
        : document.title.includes('|')
          ? document.title.slice(document.title.indexOf('|'))
          : '';
      document.title = shopSuffix ? `${titleText} ${shopSuffix.trim()}` : titleText;
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical instanceof HTMLLinkElement) {
      // Without ?variant= (a colour change can now carry the selected size in the URL).
      const canonicalUrl = new URL(productUrl, window.location.origin);
      canonical.href = canonicalUrl.origin + canonicalUrl.pathname;
    }

    const newLdJson = doc.querySelector('script[type="application/ld+json"]');
    const currentLdJson = section.querySelector('script[type="application/ld+json"]');

    if (newLdJson && currentLdJson) {
      currentLdJson.textContent = newLdJson.textContent;
    }

    const nextUrl = new URL(productUrl, window.location.origin);

    // replaceState, not pushState: picking colours must not add history entries, so one Back
    // returns to the page the shopper came from (e.g. the PLP) instead of stepping back through
    // every colour (Teamwork 41853615). The address bar still shows the chosen colour, so
    // refresh and sharing keep working.
    shownProductPath = nextUrl.pathname;
    yieldToMainThread().then(() => {
      history.replaceState({ productPdpStitching: true }, '', nextUrl.pathname + nextUrl.search);
    });
  }

  /**
   * @param {Element} section
   * @param {Document} doc
   * @param {Document} [extraDoc] - Optional additional section document (e.g. separate stitching
   *   section) whose elements are merged into the event's html so that price/availability
   *   components living in that section can still locate their own elements.
   */
  #dispatchVariantUpdate(section, doc, extraDoc) {
    const variantPicker = section.querySelector('variant-picker');
    const variantJson = variantPicker?.querySelector('script[type="application/json"]')?.textContent;

    if (!(variantPicker instanceof HTMLElement) || !variantJson) return;

    let resource;
    try {
      resource = JSON.parse(variantJson);
    } catch {
      return;
    }

    const productId = variantPicker.dataset.productId ?? '';
    const productUrl = variantPicker.dataset.productUrl ?? '';

    // Root cause of stock/availability/price never updating after a
    // cross-product swatch swap on single-variant products (confirmed
    // live: variant:update never fired at all for
    // crane-baby-plush-head-wall-decor-kendi-* swatches): variant-picker's
    // own `selectedOptionId` getter (assets/variant-picker.js) returns
    // `undefined` CLEANLY, without throwing, when there's no option input
    // to select - which is exactly the case for these single-variant
    // stitched products (no Size/etc picker is rendered when there's
    // nothing to choose). The try/catch below only ever caught a THROWN
    // error, so a clean `undefined` fell straight through to `if
    // (!sourceId) return`, silently skipping the whole event dispatch that
    // product-availability.js / product-inventory.js / product-price.js
    // all rely on to resync themselves. Falling back to the fetched
    // variant's own `resource.id` whenever selectedOptionId comes back
    // empty - not just when it throws - fixes this without changing
    // behaviour for normal multi-option products, where selectedOptionId
    // already returns a real value.
    let sourceId;
    try {
      sourceId = /** @type {{ selectedOptionId?: string }} */ (variantPicker).selectedOptionId;
    } catch {
      sourceId = undefined;
    }
    if (!sourceId) {
      sourceId = resource?.id != null ? String(resource.id) : undefined;
    }

    if (!sourceId) return;

    // When the stitching section is separate from productInfoSection, some
    // blocks (e.g. product-price, product-availability) may live in either
    // section. Build a merged lookup document that contains all elements
    // from both section responses so that every component can find its own
    // updated markup regardless of which section it is physically placed in.
    let mergedHtml = doc;
    if (extraDoc) {
      try {
        const merged = doc.cloneNode(true);
        if (merged instanceof Document) {
          const container = merged.body;
          // Append extraDoc's section content into the merged body so
          // querySelector calls on the merged doc find elements from both.
          const extraRoot = extraDoc.body.firstElementChild;
          if (extraRoot) container.appendChild(extraRoot.cloneNode(true));
          mergedHtml = merged;
        }
      } catch {
        // Merging failed — fall back to primary doc only.
      }
    }

    const eventData = {
      html: mergedHtml,
      productId,
      newProduct: productId && productUrl ? { id: productId, url: productUrl } : undefined,
    };

    // 1. Section-scoped dispatch: components inside productInfoSection
    //    catch this via their closest('.shopify-section') listener.
    variantPicker.dispatchEvent(new VariantUpdateEvent(resource, sourceId, eventData));

    // 2. Document-level dispatch: components that live in a DIFFERENT
    //    section (e.g. a separately-placed sticky-add-to-cart section, a
    //    price block inside the stitching section) also need to react.
    //    product-price.js / sticky-add-to-cart.js guard against wrong-
    //    product updates via event.detail.data.newProduct.id matching, so
    //    a document-level re-dispatch is safe here.
    document.dispatchEvent(new VariantUpdateEvent(resource, sourceId, eventData));
  }
}

if (!customElements.get('product-pdp-stitching')) {
  customElements.define('product-pdp-stitching', ProductPdpStitching);
}
